import { GenerateReportRequest } from 'features/lead-management/types/request';

import { END_POINTS } from '@config';
import { api } from '@core/baseApi';
import { JobStatusEnum } from '@features/lead-management/constants/lead-report';
import {
  ReportMetadataRequest,
  ReportMetadataResponse
} from '@features/reports/types';
import { HTTPResponse, OrgAndBranchId } from '@types';

const extendedBaseQuery = api.injectEndpoints({
  endpoints: (builder) => ({
    downloadGstReport: builder.query<
      HTTPResponse<any>,
      { params: OrgAndBranchId; queryString: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.REPORTS.GST_REPORT(req.params)}${req.queryString}`,
        method: 'GET',
        headers: {
          'Content-Type':
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          // Get the filename from Content-Disposition header
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          let filename = 'gst-report.xlsx'; // Default fallback

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            );
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          }
          return { blob, filename };
        }
      })
    }),
    downloadCustomReport: builder.query<
      HTTPResponse<any>,
      { params: OrgAndBranchId; queryString: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.REPORTS.REPORTS(req.params)}${req.queryString}`,
        method: 'GET',
        headers: {
          'Content-Type':
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          // Get the filename from Content-Disposition header
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          let filename = 'custom-report.xlsx'; // Default fallback

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            );
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          }
          return { blob, filename };
        }
      })
    }),
    getDownloadFileStatus: builder.query<HTTPResponse<any>, { jobId: string }>({
      query: (req) => ({
        url: `${END_POINTS.REPORTS.GET_DOWNLOAD_FILE_STATUS(req.jobId)}`,
        method: 'GET'
      })
    }),
    getCustomReportJobId: builder.query<
      HTTPResponse<any>,
      { params: OrgAndBranchId; queryString: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.REPORTS.CUSTOM_REPORT_JOB_ID(req.params)}${
          req.queryString
        }`,
        method: 'GET'
      })
    }),
    getCustomReportJobIdV3: builder.mutation<
      HTTPResponse<any>,
      { params: OrgAndBranchId; body: Record<string, unknown> }
    >({
      query: ({ params, body }) => ({
        url: END_POINTS.REPORTS.CUSTOM_REPORT_JOB_ID_V3(params),
        method: 'POST',
        body
      })
    }),
    getExportMembersJobId: builder.mutation<
      HTTPResponse<any>,
      {
        params: OrgAndBranchId;
        body: {
          group_ids: string[];
          include_non_group_members: boolean;
          columns: string[];
        };
      }
    >({
      query: ({ params, body }) => ({
        url: `${END_POINTS.REPORTS.MEMBERS_EXPORT(params)}`,
        method: 'POST',
        body
      })
    }),
    getGstReportJobId: builder.query<
      HTTPResponse<any>,
      { params: OrgAndBranchId; queryString: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.REPORTS.GST_REPORT_JOB_ID(req.params)}${
          req.queryString
        }`,
        method: 'GET'
      })
    }),
    getChargesReportJobId: builder.query<
      HTTPResponse<any>,
      { params: OrgAndBranchId; queryString: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.REPORTS.CHARGES_REPORT(req.params)}${
          req.queryString
        }`,
        method: 'GET'
      })
    }),
    getAuditLogJobId: builder.query<
      { job_id: string },
      { organizationId: string; queryString: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.REPORTS.AUDIT_LOG_EXPORT(req.organizationId)}${
          req.queryString
        }`,
        method: 'GET'
      })
    }),
    reportMetadata: builder.mutation<
      HTTPResponse<ReportMetadataResponse>,
      { organizationId: string; body: ReportMetadataRequest }
    >({
      query: ({ organizationId, body }) => ({
        url: END_POINTS.REPORTS.REPORT_METADATA(organizationId),
        method: 'POST',
        body
      })
    }),
    // Lead Management
    generateLeadReport: builder.mutation<
      HTTPResponse<{ status: string }>,
      { organizationId: string; payload: GenerateReportRequest }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GENERATE_REPORT(req.organizationId),
        method: 'POST',
        body: req.payload
      })
    }),
    getLeadReportJobStatus: builder.query<
      HTTPResponse<{
        status: JobStatusEnum;
        signed_url?: string;
        error?: string;
      }>,
      { jobId: string; organizationId: string }
    >({
      query: (req) => ({
        url: END_POINTS.LEAD_MANAGEMENT.GET_REPORT_JOB_STATUS(
          req.jobId,
          req.organizationId
        ),
        method: 'GET'
      })
    })
  })
});

// eslint-disable-next-line import/prefer-default-export
export const {
  useLazyDownloadGstReportQuery,
  useLazyDownloadCustomReportQuery,
  useLazyGetDownloadFileStatusQuery,
  useGetCustomReportJobIdV3Mutation,
  useLazyGetCustomReportJobIdQuery,
  useGetExportMembersJobIdMutation,
  useLazyGetGstReportJobIdQuery,
  useLazyGetChargesReportJobIdQuery,
  useLazyGetAuditLogJobIdQuery,
  useReportMetadataMutation,

  // Lead Management
  useGenerateLeadReportMutation,
  useLazyGetLeadReportJobStatusQuery
} = extendedBaseQuery;
